import { createContext, useContext, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { IntegrationsProvider } from 'contexts/IntegrationsContext'
import { IMainState } from 'store/states'
import { BlockTypes } from 'enum/block-type.enum'
import { IEvaluationQuestion } from 'models/evaluation.model'
import {
  BlockSurveyContent,
  BlockTaskContent,
} from 'store/states/lessons.state'
import { blockHasResponse } from 'helpers/validators.helper'
import { CourseStateType } from 'enum'
import { useMultimediaProgress, useIsLastLessonHigher } from './hooks'

interface ViewContextProps {
  multimediaProgress: {
    finished: boolean
  }
  isLastLessonHigher: boolean
  canAnswer: boolean
  previewMode: boolean
  enrolled: boolean
  imported: boolean
  validations: {
    lesson: {
      everyHasResponse: boolean
      everyEvaluationsHasResponse: boolean
      everyTasksHasResponse: boolean
    }
  }
}

export const CourseContext = createContext<ViewContextProps>(
  Object.create(null)
)

export const CourseContextConsumer = CourseContext.Consumer

export const useCourse = () => {
  const context = useContext(CourseContext)
  if (context === undefined) {
    throw new Error('useCourseContext must be used within a CourseProvider')
  }
  return context
}

export const CourseProvider: React.FC<{ previewMode?: boolean }> = ({
  children,
  previewMode = false,
}) => {
  const multimediaProgress = useMultimediaProgress()
  const isLastLessonHigher = useIsLastLessonHigher()
  const enrol = useSelector(({ courses }: IMainState) => courses.course?.enrol)
  const finishDate = enrol?.finishDate

  const courseState = useSelector(
    ({ courses }: IMainState) => courses.course?.state
  )

  const isImparted = courseState === CourseStateType.Imparted

  const { currentLessonContent } = useSelector(
    ({ lmsLessons }: IMainState) => lmsLessons
  )

  const blockEvaluations = useMemo(
    () =>
      currentLessonContent.filter(
        (block) => block.type === BlockTypes.BlockEvaluation
      ) as IEvaluationQuestion[],
    [currentLessonContent]
  )

  const blockTasks = useMemo(
    () =>
      currentLessonContent.filter(
        (block) => block.type === BlockTypes.BlockTask
      ) as BlockTaskContent[],
    [currentLessonContent]
  )

  const blockSurveys = useMemo(
    () =>
      currentLessonContent.filter(
        (block) => block.type === BlockTypes.BlockSurvey
      ) as BlockSurveyContent[],
    [currentLessonContent]
  )

  // La lección actual tiene todos las evaluaciones obligatorios completados?
  const everyEvaluationsHasResponse = useMemo(
    () => blockEvaluations.every((block) => blockHasResponse(block)),
    [blockEvaluations]
  )

  // La lección actual tiene todos las tareas obligatorios completados
  const everyTasksHasResponse = useMemo(
    () =>
      blockTasks
        .filter((task) => task.mandatory)
        .every((block) => blockHasResponse(block)),
    [blockTasks]
  )

  // La lección actual tiene todos las encuestas obligatorios completados
  const everySurveysHasResponse = useMemo(
    () => blockSurveys.every((block) => blockHasResponse(block)),
    [blockSurveys]
  )

  const everyHasResponse =
    everyEvaluationsHasResponse &&
    everyTasksHasResponse &&
    everySurveysHasResponse

  return (
    <CourseContext.Provider
      value={{
        multimediaProgress,
        isLastLessonHigher,
        // Si no tiene fecha de finalización puede responder y si el curso no está impartido
        canAnswer: !finishDate && !isImparted,
        previewMode,
        enrolled: !!enrol?.id,
        imported: !!enrol?.imported,
        validations: {
          lesson: {
            everyHasResponse,
            everyEvaluationsHasResponse,
            everyTasksHasResponse,
          },
        },
      }}
    >
      <IntegrationsProvider>{children}</IntegrationsProvider>
    </CourseContext.Provider>
  )
}

export * from './hooks'
